import React, { forwardRef, useMemo } from 'react';
import { Card, styled, Typography } from '@mui/material';
import { DistributiveOmit } from 'react-redux';
import { Button } from 'components/Button/Button';
import { AssessmentsQuestionProps, TriggerSkipLogic } from 'features/assessments/types';
import { useLastQuestionMargin } from 'features/assessments/hooks/useLastQuestionMargin';
import { decodeAndSanitizeAssessmentHtml } from 'utils/contentUtils';
import { useQuestionCardState } from 'features/assessments/hooks/useQuestionCardState';
import { RequiredAsterisk } from './AssessmentsRequiredAsterisk';
import { AssessmentsQuestionField } from './AssessmentsQuestionField';
import { AssessmentsQuestionErrorInfo } from './AssessmentsQuestionErrorInfo';
import { AssessmentsQuestionLastQuestion } from './AssessmentsQuestionLastQuestion';

const BaseQuestionCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
}));

const QuestionCard = styled(BaseQuestionCard, {
  shouldForwardProp: (prop) => prop !== 'error' && prop !== 'active' && prop !== 'isSkipped',
})<{ error: boolean; active: boolean; isSkipped: boolean }>(
  ({ theme, variant, error, active, isSkipped }) => ({
    boxShadow: 'none',
    ...(active && {
      boxShadow: theme.shadows[4],
    }),
    ...(variant === 'outlined' && {
      border: `${error ? '1px' : '0px'} dashed ${theme.palette.error.main}`,
    }),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4),
    ...(isSkipped && {
      pointerEvents: 'none',
    }),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 3),
    },
  }),
);

const SubQuestionCard = styled(BaseQuestionCard)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2, 3),
}));

const QuestionText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isSkipped',
})<{ isSkipped: boolean }>(({ theme, isSkipped }) => ({
  color: theme.palette.grey[800],
  opacity: isSkipped ? 0.5 : 1,
}));

const QuestionTextSpan = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isSkipped',
})<{ isSkipped: boolean }>(({ theme, isSkipped }) => ({
  color: theme.palette.grey[800],
  opacity: isSkipped ? 0.5 : 1,
}));

const NextButton = styled(Button)(({ theme }) => ({
  paddingLeft: theme.spacing(15),
  paddingRight: theme.spacing(15),
  alignSelf: 'center',
  marginTop: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
}));

type AssessmentsQuestionComponentProps = DistributiveOmit<
  AssessmentsQuestionProps,
  'questionLabelId'
> & {
  onNextClick?: TriggerSkipLogic;
  isSubQuestion?: boolean;
  formValue?: String[] | string | undefined;
  isLastQuestion?: boolean;
  isLastPage?: boolean;
  onSubmit?: () => void;
  isSubmitDisabled?: boolean;
};

export const AssessmentsQuestion = forwardRef<HTMLDivElement, AssessmentsQuestionComponentProps>(
  (props, ref) => {
    const {
      id,
      type,
      questionText,
      questionNumber,
      isRequired,
      onNextClick,
      isSubQuestion,
      nextButtonDisabled,
      showNextButton,
      formValue,
      isLastQuestion,
      isLastPage,
      onSubmit,
      isSubmitDisabled,
    } = props;

    const questionLabelId = `assessment-question-${id}`;
    const isLastQuestionOnLastPage = Boolean(isLastQuestion && isLastPage);
    const shouldShowNextButton = Boolean(showNextButton && !isLastQuestionOnLastPage);

    const { getQuestionCardState } = useQuestionCardState();

    const { isSkipped, isActive, cardVariant, cardTabIndex, cardAriaDisabled, handleCardFocus } =
      useMemo(
        () =>
          getQuestionCardState({
            id,
            questionNumber,
            isSubQuestion,
            type,
          }),
        [getQuestionCardState, id, questionNumber, isSubQuestion, type],
      );

    const { setCardRef, bottomMargin } = useLastQuestionMargin({
      ref,
      isLastQuestion,
      isLastPage,
      isSkipped,
      formValue,
      type,
      questionText,
      showNextButton: shouldShowNextButton,
    });

    // Transform subQuestions to rendered children for radio/checkbox options
    const transformedProps = useMemo(() => {
      if (type === 'radio' || type === 'checkbox') {
        const { options, ...rest } = props;
        return {
          ...rest,
          options: options.map((option) => ({
            ...option,
            children: option.subQuestions?.map((subQ) => (
              <AssessmentsQuestion key={subQ.id} {...subQ} isSubQuestion />
            )),
          })),
        };
      }
      return props;
    }, [props, type]);

    const renderQuestionText = useMemo(() => {
      if (!questionText) return null;

      return (
        <QuestionText id={questionLabelId} variant="body1" isSkipped={isSkipped}>
          {isSubQuestion || questionNumber === undefined ? '' : `${questionNumber}. `}
          <QuestionTextSpan
            id={questionLabelId}
            isSkipped={isSkipped}
            dangerouslySetInnerHTML={{ __html: decodeAndSanitizeAssessmentHtml(questionText) }}
          />
          {isRequired ? <RequiredAsterisk aria-hidden="true">*</RequiredAsterisk> : null}
        </QuestionText>
      );
    }, [questionText, type, questionLabelId, isSkipped, isRequired, isSubQuestion, questionNumber]);

    const withQuestion = useMemo(
      () => (
        <>
          <AssessmentsQuestionErrorInfo showError={type === 'unsupported'} />

          {renderQuestionText}
          {!isSkipped ? (
            <>
              <AssessmentsQuestionField {...transformedProps} questionLabelId={questionLabelId} />
              {/* Next Button for non-deterministic answer types */}
              {shouldShowNextButton && (
                <NextButton
                  variant="primary"
                  size="xlarge"
                  onClick={() =>
                    onNextClick?.({ currentQuestionId: id, value: formValue, performScroll: true })
                  }
                  disabled={nextButtonDisabled}
                >
                  Next
                </NextButton>
              )}
            </>
          ) : null}
        </>
      ),
      [
        transformedProps,
        type,
        renderQuestionText,
        isSkipped,
        shouldShowNextButton,
        onNextClick,
        nextButtonDisabled,
        questionLabelId,
      ],
    );

    if (isSubQuestion) {
      return <SubQuestionCard variant="outlined">{withQuestion}</SubQuestionCard>;
    }

    const card = (
      <QuestionCard
        ref={setCardRef}
        variant={cardVariant}
        error={type === 'unsupported'}
        active={isActive}
        isSkipped={isSkipped}
        tabIndex={cardTabIndex}
        aria-disabled={cardAriaDisabled}
        onFocus={handleCardFocus}
      >
        {withQuestion}
      </QuestionCard>
    );
    if (isLastQuestion) {
      return (
        <AssessmentsQuestionLastQuestion
          bottomMargin={bottomMargin}
          card={card}
          showSubmitButton={Boolean(isLastQuestionOnLastPage && !isSkipped)}
          onSubmit={onSubmit}
          isSubmitDisabled={isSubmitDisabled}
        />
      );
    }

    return card;
  },
);

AssessmentsQuestion.displayName = 'AssessmentsQuestion';
